import React from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  AlertCircle,
  FileQuestion,
  Hash,
  HelpCircle,
  ListFilter,
  Video as VideoIcon,
} from 'lucide-react';

interface PreScreenResult {
  questionId: string;
  answer: string | number;
  questionType: 'numeric' | 'multiple-choice' | 'location' | 'text' | 'video';
  question: string;
  videoKey?: string;
  videoUrl?: string;
}

interface IPreScreenResults {
  preScreenResults: PreScreenResult[];
  loading: boolean;
  error: string | null;
}

export const PreScreenResults: React.FC<IPreScreenResults> = ({
  preScreenResults,
  loading,
  error,
}) => {
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress color="primary" />
          <Typography variant="body2" color="text.secondary">
            Loading pre-screen results...
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          bgcolor: 'error.50',
          border: 1,
          borderColor: 'error.light',
          borderRadius: 2,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <AlertCircle size={24} color="#dc2626" />
          <Box>
            <Typography variant="subtitle1" fontWeight={700} color="error.dark">
              Unable to Load Results
            </Typography>
            <Typography variant="body2" color="error.main">
              {error}
            </Typography>
          </Box>
        </Stack>
      </Paper>
    );
  }

  if (!preScreenResults || preScreenResults.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 6,
          textAlign: 'center',
          border: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          borderRadius: 2,
        }}
      >
        <Stack spacing={2} alignItems="center" maxWidth={400} mx="auto">
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              bgcolor: 'action.hover',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <FileQuestion size={28} color="#94a3b8" />
          </Box>
          <Typography variant="h6" fontWeight={600} color="text.primary">
            No Pre-Screen Questions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This assessment doesn&apos;t include any pre-screening questions or
            the results are not yet available.
          </Typography>
        </Stack>
      </Paper>
    );
  }

  const getQuestionTypeChip = (type: string) => {
    switch (type) {
      case 'numeric':
        return (
          <Chip
            icon={<Hash size={12} />}
            label="Numeric"
            size="small"
            color="success"
            variant="outlined"
            sx={{ fontSize: '0.65rem', fontWeight: 700 }}
          />
        );
      case 'multiple-choice':
        return (
          <Chip
            icon={<ListFilter size={12} />}
            label="Multiple Choice"
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontSize: '0.65rem', fontWeight: 700 }}
          />
        );
      case 'video':
        return (
          <Chip
            icon={<VideoIcon size={12} />}
            label="Video"
            size="small"
            color="secondary"
            variant="outlined"
            sx={{ fontSize: '0.65rem', fontWeight: 700 }}
          />
        );
      default:
        return (
          <Chip
            icon={<HelpCircle size={12} />}
            label={type}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.65rem', fontWeight: 700 }}
          />
        );
    }
  };

  return (
    <Stack spacing={2}>
      <Paper
        elevation={0}
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2.5,
            py: 1.75,
            bgcolor: 'action.hover',
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="subtitle1" fontWeight={700}>
            Pre-Screen Results
          </Typography>
          <Chip
            label={`${preScreenResults.length} Completed`}
            size="small"
            color="primary"
            sx={{ fontWeight: 600, fontSize: '0.75rem' }}
          />
        </Box>

        {/* Results List */}
        <Stack divider={<Box sx={{ borderBottom: 1, borderColor: 'divider' }} />}>
          {preScreenResults.map((result, index) => (
            <Box
              key={result.questionId || index}
              sx={{
                p: 2.5,
                transition: 'background-color 0.2s',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'primary.50',
                    border: 1,
                    borderColor: 'primary.100',
                    flexShrink: 0,
                    mt: 0.25,
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color="primary.main"
                    fontSize="0.7rem"
                  >
                    {index + 1}
                  </Typography>
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="text.secondary"
                      textTransform="uppercase"
                    >
                      Question
                    </Typography>
                    {getQuestionTypeChip(result.questionType)}
                  </Stack>

                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color="text.primary"
                    mb={2}
                  >
                    {result.question}
                  </Typography>

                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      bgcolor: 'background.default',
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="text.secondary"
                      textTransform="uppercase"
                    >
                      Answer
                    </Typography>
                    <Box sx={{ flex: 1 }}>
                      {result.questionType === 'numeric' ? (
                        <Chip
                          label={result.answer}
                          size="small"
                          color="success"
                          sx={{ fontWeight: 700 }}
                        />
                      ) : result.questionType === 'video' ? (
                        result.videoUrl ||
                        (typeof result.answer === 'string' &&
                          result.answer.startsWith('http')) ? (
                          <Box
                            component="video"
                            src={result.videoUrl || String(result.answer)}
                            preload="metadata"
                            controls
                            sx={{
                              width: '100%',
                              maxWidth: 360,
                              borderRadius: 1.5,
                              display: 'block',
                            }}
                          />
                        ) : (
                          <Chip
                            icon={<VideoIcon size={12} />}
                            label="Video submitted"
                            size="small"
                            color="secondary"
                            variant="outlined"
                          />
                        )
                      ) : (
                        <Typography variant="body2" fontWeight={600} color="primary.main">
                          {result.answer}
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                </Box>
              </Stack>
            </Box>
          ))}
        </Stack>
      </Paper>
    </Stack>
  );
};
